/**
 * The override kinds the Forecast Details inline table shows, each read from
 * the API that already owns it. Nothing here is a new endpoint or a new write
 * rule: a quick edit calls the same create / update mutation the full editor
 * does, so the audit trail (the row chain plus the CRUD change log) is the same
 * whichever surface the buyer used.
 *
 * To add an override kind, write one more `use…InlineFields` hook that returns
 * `InlineOverrideField[]` and list it in `useInlineOverrideFields`.
 */

import { useGT } from 'gt-react';
import {
  FORECAST_FACTOR_MAX,
  FORECAST_FACTOR_MIN,
  LEAD_TIME_OVERRIDE_DAYS_MAX,
  LEAD_TIME_OVERRIDE_DAYS_MIN,
} from '@erp/shared-types';
import type {
  EffectivePolicy,
  ForecastFactorRow,
  InventoryPolicyOverrideRow,
  LeadTimeOverrideRow,
} from '@erp/rpc';
import {
  useCreateInventoryPolicyOverride,
  useInventoryPolicyOverrides,
  useUpdateInventoryPolicyOverride,
} from 'src/api/rpc/hooks/procurement/useInventoryPolicyOverrides';
import {
  useCreateLeadTimeOverride,
  useLeadTimeOverrides,
  useUpdateLeadTimeOverride,
} from 'src/api/rpc/hooks/procurement/useLeadTimeOverrides';
import {
  useCreateForecastFactor,
  useForecastFactors,
  useUpdateForecastFactor,
} from 'src/api/rpc/hooks/procurement/useForecastFactors';
import { useLeadTimeDetail } from '@/api/rpc/hooks/procurement/useLeadTimeDetail';
import { overrideFieldLabel } from '../../../replenishment/suppliers/policyOverrideRows';
import {
  factorToPercent,
  inEffectFactor,
  percentToFactor,
} from '../../../replenishment/suppliers/forecastFactorRows';
import {
  governingPolicyOverride,
  INLINE_POLICY_FIELDS,
  pinsOnly,
  type GoverningOverride,
  type InlineOverrideField,
  type InlinePolicyField,
} from './inlineOverrideField';

type Translate = ReturnType<typeof useGT>;

export interface InlineOverrideTarget {
  warehouseId: string;
  productV3Key: string;
}

function governing<
  R extends {
    id: string;
    startsAt: string;
    endsAt: string | null;
    reviewAt: string | null;
    needsReview: boolean;
    reason: string | null;
    createdByUserName: string | null;
    createdAt: string;
  },
>(row: R, value: number): GoverningOverride {
  return {
    id: row.id,
    value,
    startsAt: row.startsAt,
    endsAt: row.endsAt,
    reviewAt: row.reviewAt,
    needsReview: row.needsReview,
    reason: row.reason,
    createdByUserName: row.createdByUserName,
    createdAt: row.createdAt,
  };
}

function nonNegative(gt: Translate) {
  return (value: number) =>
    value < 0
      ? gt('Enter zero or more.', { context: 'inline override validation' })
      : null;
}

// ─── Inventory policy: min, max, safety stock, min floor, pack qty ──────────

/** Engine (base) value and the effective value, per policy field. */
function policyValues(
  effective: EffectivePolicy | null,
  field: InlinePolicyField,
): { calculated: number | null; effective: number | null } {
  if (!effective) return { calculated: null, effective: null };
  switch (field) {
    case 'minQuantity':
      return {
        calculated: effective.baseMinQuantity,
        effective: effective.minQuantity,
      };
    case 'maxQuantity':
      return {
        calculated: effective.baseMaxQuantity,
        effective: effective.maxQuantity,
      };
    case 'safetyStock':
      return {
        calculated: effective.baseSafetyStock,
        effective: effective.safetyStock,
      };
    case 'hardMin':
    case 'hardMax':
      return { calculated: null, effective: effective[field] };
    case 'minFloor':
      // A floor is buyer-owned: the engine never sets one.
      return { calculated: null, effective: effective.minFloor };
    case 'orderMultiple':
      // No policy base: the master is the vendor MOQ/increment it replaces.
      return { calculated: null, effective: effective.orderMultiple };
  }
}

/** A pack qty of zero would round every buy to nothing. */
function positivePackQty(gt: Translate) {
  return (value: number) =>
    value > 0
      ? null
      : gt('Pack qty must be greater than zero.', {
          context: 'inline override validation',
        });
}

function policyValidator(
  field: InlinePolicyField,
  effective: EffectivePolicy | null,
  gt: Translate,
) {
  if (field === 'orderMultiple') return positivePackQty(gt);
  const base = nonNegative(gt);
  return (value: number) => {
    const invalid = base(value);
    if (invalid) return invalid;
    if (field === 'minQuantity' && effective && value > effective.maxQuantity) {
      return gt('Min cannot exceed the current max ({max}).', {
        context: 'inline override validation',
        max: effective.maxQuantity.toLocaleString(),
      });
    }
    if (field === 'maxQuantity' && effective && value < effective.minQuantity) {
      return gt('Max cannot be below the current min ({min}).', {
        context: 'inline override validation',
        min: effective.minQuantity.toLocaleString(),
      });
    }
    return null;
  };
}

function policyLockedReason(
  field: InlinePolicyField,
  effective: EffectivePolicy | null,
  gt: Translate,
): string | null {
  if (!effective) {
    return gt('No inventory policy exists here yet.', {
      context: 'inline override locked reason',
    });
  }
  if (field === 'safetyStock' && effective.baseSafetyStock === null) {
    return gt('Available once a forecast has calculated safety stock.', {
      context: 'inline override locked reason',
    });
  }
  return null;
}

export function usePolicyInlineFields(
  target: InlineOverrideTarget,
): InlineOverrideField[] {
  const gt = useGT();
  const query = useInventoryPolicyOverrides(target);
  const create = useCreateInventoryPolicyOverride();
  const update = useUpdateInventoryPolicyOverride();
  const effective = query.data?.effective ?? null;
  const overrides: readonly InventoryPolicyOverrideRow[] =
    query.data?.overrides ?? [];
  const kind = gt('Policy', { context: 'inline override kind' });

  return INLINE_POLICY_FIELDS.map((field) => {
    const row = governingPolicyOverride(overrides, field);
    const pinned = row?.[field] ?? null;
    // Replace in place only a row that pins this field alone; otherwise a
    // newer single-field row shadows it (per-field last-writer-wins), leaving
    // the other fields it pins untouched.
    const replaces = row !== null && pinsOnly(row, field);
    return {
      id: `policy.${field}`,
      kind,
      label: overrideFieldLabel(field, gt),
      unit: 'units',
      ...policyValues(effective, field),
      override: row && pinned !== null ? governing(row, pinned) : null,
      grain: 'day',
      lockedReason: policyLockedReason(field, effective, gt),
      validate: policyValidator(field, effective, gt),
      replaces,
      save: ({ value, window, startsLater }) =>
        replaces && row && !startsLater
          ? update.mutateAsync({
              overrideId: row.id,
              [field]: value,
              ...window,
            })
          : create.mutateAsync({ ...target, [field]: value, ...window }),
      pending: create.isPending || update.isPending,
    };
  });
}

// ─── Lead time ──────────────────────────────────────────────────────────────

// CRAP is estimated from direct export references; inlineOverrideSources.test.ts
// covers this through useInlineOverrideFields.
// fallow-ignore-next-line complexity
export function useLeadTimeInlineFields(
  target: InlineOverrideTarget,
): InlineOverrideField[] {
  const gt = useGT();
  const list = useLeadTimeOverrides(target);
  const detail = useLeadTimeDetail(target);
  const create = useCreateLeadTimeOverride();
  const update = useUpdateLeadTimeOverride();
  const data = detail.data;
  // The row the resolver used; the list's `active` is the same row, and is
  // there before the detail query when that one is slower.
  const row: LeadTimeOverrideRow | null =
    data?.override ?? list.data?.active ?? null;
  const observed = data?.observed.leadTimeDays ?? null;
  const resolved = data?.resolved.leadTimeDays ?? null;

  const calculatedHint = !data
    ? undefined
    : observed !== null
      ? gt('observed, {n} receipts', {
          context: 'inline override lead time provenance',
          n: data.observed.sampleN,
        })
      : gt('assumed, no receipts', {
          context: 'inline override lead time provenance',
        });

  return [
    {
      id: 'leadTime.days',
      kind: gt('Lead time', { context: 'inline override kind' }),
      label: gt('Lead Time', { context: 'inline override field label' }),
      unit: 'days',
      calculated: observed ?? data?.fallbackDays ?? null,
      calculatedHint,
      effective: resolved ?? data?.fallbackDays ?? null,
      override: row ? governing(row, row.leadTimeDays) : null,
      grain: 'day',
      lockedReason: null,
      validate: (value) =>
        value < LEAD_TIME_OVERRIDE_DAYS_MIN ||
        value > LEAD_TIME_OVERRIDE_DAYS_MAX
          ? gt('Whole days, {min} to {max}.', {
              context: 'inline override validation',
              min: LEAD_TIME_OVERRIDE_DAYS_MIN,
              max: LEAD_TIME_OVERRIDE_DAYS_MAX,
            })
          : null,
      replaces: row !== null,
      save: ({ value, window, startsLater }) =>
        row && !startsLater
          ? update.mutateAsync({
              overrideId: row.id,
              leadTimeDays: value,
              ...window,
            })
          : create.mutateAsync({ ...target, leadTimeDays: value, ...window }),
      pending: create.isPending || update.isPending,
    },
  ];
}

// ─── Growth / shrinkage factor ──────────────────────────────────────────────

// CRAP is estimated from direct export references; inlineOverrideSources.test.ts
// covers this through useInlineOverrideFields.
// fallow-ignore-next-line complexity
export function useGrowthInlineFields(
  target: InlineOverrideTarget,
): InlineOverrideField[] {
  const gt = useGT();
  const list = useForecastFactors(target);
  const create = useCreateForecastFactor();
  const update = useUpdateForecastFactor();
  const overrides: readonly ForecastFactorRow[] = list.data?.overrides ?? [];
  // The factor scaling this month, not the newest row (which may be scheduled).
  const row = inEffectFactor(overrides, new Date());
  const pct = row ? factorToPercent(row.factor) : null;

  return [
    {
      id: 'growth.factor',
      kind: gt('Growth', { context: 'inline override kind' }),
      label: gt('Growth Factor', { context: 'inline override field label' }),
      unit: 'percent',
      // The statistical forecast, unscaled.
      calculated: list.data ? 0 : null,
      effective: list.data ? (pct ?? 0) : null,
      override: row && pct !== null ? governing(row, pct) : null,
      grain: 'month',
      lockedReason: null,
      validate: (value) => {
        const factor = percentToFactor(value);
        if (factor === 1) {
          return gt('Enter a growth or a shrinkage, not 0%.', {
            context: 'inline override validation',
          });
        }
        return factor < FORECAST_FACTOR_MIN || factor > FORECAST_FACTOR_MAX
          ? gt('Out of range.', { context: 'inline override validation' })
          : null;
      },
      replaces: row !== null,
      save: ({ value, window, startsLater }) => {
        const factor = percentToFactor(value);
        return row && !startsLater
          ? update.mutateAsync({ overrideId: row.id, factor, ...window })
          : create.mutateAsync({ ...target, factor, ...window });
      },
      pending: create.isPending || update.isPending,
    },
  ];
}

/**
 * Every row the inline table shows, in display order. Each source is its own
 * hook call so the hook order stays fixed across renders.
 */
/**
 * Whether every source behind the fields has answered. A field whose source
 * has not loaded reads as "no override", so a count of overrides is only a
 * claim once this says `ready`. The reads are the fields' own, deduplicated
 * by the query cache.
 */
export function useInlineOverrideStatus(
  target: InlineOverrideTarget,
): 'loading' | 'error' | 'ready' {
  const reads = [
    useInventoryPolicyOverrides(target),
    useLeadTimeOverrides(target),
    useLeadTimeDetail(target),
    useForecastFactors(target),
  ];
  if (reads.some((read) => read.isError)) return 'error';
  if (reads.some((read) => read.data === undefined)) return 'loading';
  return 'ready';
}

export function useInlineOverrideFields(
  target: InlineOverrideTarget,
): InlineOverrideField[] {
  const policy = usePolicyInlineFields(target);
  const leadTime = useLeadTimeInlineFields(target);
  const growth = useGrowthInlineFields(target);
  return [...policy, ...leadTime, ...growth];
}
